<template>
  <div class="content">
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <!--  Feature not enabled-->
    <div v-if="feature_not_enabled" class="mt-3" >
      <div class="font-icon-detail">
            <span style="font-size: 3em">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
        <h4 class="mt-3">This feature is unavailable.</h4>
        <base-button type="primary" v-if="hasPermissions('billing_view')"
                     v-on:click="goToBilling()" class="mt-3">Go to billing</base-button>
        <p style="font-size: medium" v-else>Contact a site administrator to update billing.</p>
      </div>
    </div>

    <div v-if="!feature_not_enabled">
      <div class="row overflow-scroll">
        <div class="col-12">
          <card card-body-classes="table-full-width">
            <h4 slot="header" class="card-title">Waivers</h4>

            <filter-bar
              ref="filterBar"
              :key="filterBarKey"
              :filters="filterSchema"
              :initial-value="filterValues"
              :result-count="totalRows"
              :loading="refreshing"
              :per-page="perPage"
              :per-page-options="perPageOptions"
              :quick-actions="quickActions"
              saved-filters-key="waivers"
              @change="onFiltersChanged"
              @update:perPage="onPerPageChange"
              @quick-action="onQuickAction"
            >
              <template #actions>
                <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" @click="downloadCSV()">
                  <i class="tim-icons icon-cloud-download-93"></i>
                </base-button>
              </template>
            </filter-bar>
            <div v-if="noDataAvailable === true" class="text-center py-5">
              <img class="empty-state-img empty-state-img--md mb-3" src="/img/pandas/waiver.png" alt="" />
              <h4 class="mb-2 text-white">No waivers found</h4>
              <p class="empty-state-hint mb-3">Try adjusting your filters or clearing your search.</p>
              <base-button size="sm" type="primary" simple @click="clearFilters()">
                Clear filters
              </base-button>
            </div>
            <div v-if="noDataAvailable === false" class="row d-none d-sm-block">
              <div v-if="load" class="col-md-12 col-lg-12 col-sm-12 col-12">
                <b-table hover :items="waivers"
                          :current-page="currentPage"
                          :fields="fields"
                          :tbody-tr-class="rowClass"
                          @row-clicked="onRowClicked">

                  <template v-slot:cell(name)="row">
                    <span>{{row.item.first_name}} {{row.item.last_name}}</span>
                    &nbsp;<badge v-if="row.item.is_minor === 1" type="primary">minor</badge>
                    <badge v-if="row.item.not_participating === 1" type="danger">not participating</badge>
                    <badge v-if="row.item.custom_field_response_count > 0" type="default">
                      {{ row.item.custom_field_response_count }} {{ row.item.custom_field_response_count === 1 ? 'response' : 'responses' }}
                    </badge>
                  </template>
                  <template v-slot:cell(phone)="row">
                    <span>{{row.item.phone ? formatPhone(row.item.phone) : '-'}}</span>
                  </template>
                  <template v-slot:cell(game)="row">
                    <span>{{row.item.game || 'Walk-in'}}</span>
                    <badge v-if="!row.item.booking_slot_id && row.item.game" type="default" class="ml-2" style="font-size: 8px; padding-left: 7px; padding-right: 7px;">No booking</badge>
                  </template>
                  <template v-slot:cell(booking_day)="row">
                    <span>{{getDayOfWeek(row.item.booking_date)}} {{getDate(row.item.booking_date)}}</span>
                  </template>
                  <template v-slot:cell(booking_time)="row">
                    <span>{{getDisplayTime(row.item)}}</span>
                  </template>
                </b-table>
                <b-pagination v-on:input="onPageChange()"
                              v-model="currentPage"
                              :total-rows="totalRows"
                              :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
            <div v-if="noDataAvailable === false" class="row d-block d-sm-none waivers-mobile-wrap">
              <div v-if="load && loading" class="col-md-12 col-lg-12 col-sm-12 col-12">
                <div class="waiver-skel-list">
                  <div v-for="n in 5" :key="'wsk' + n" class="waiver-skel-card">
                    <div class="d-flex justify-content-between align-items-start" style="gap: 12px;">
                      <div style="flex: 1; min-width: 0;">
                        <div class="skel skel--text" style="width: 55%; height: 16px;"></div>
                        <div class="skel skel--text mt-2" style="width: 35%; height: 12px;"></div>
                      </div>
                      <span class="skel skel--badge"></span>
                    </div>
                    <div class="d-flex align-items-center mt-2" style="gap: 10px;">
                      <div class="skel" style="width: 40px; height: 40px; border-radius: 6px;"></div>
                      <div class="skel skel--text" style="width: 50%; height: 12px;"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="load" class="col-md-12 col-lg-12 col-sm-12 col-12">
                <b-table hover :items="waivers"
                          :current-page="currentPage"
                          :fields="mobile_fields"
                          :tbody-tr-class="rowClass"
                          @row-clicked="onRowClicked">
                  <template v-slot:head(waivers)="row">
                    <span>Total results: {{totalRows}}</span>
                  </template>
                  <template v-slot:cell(waivers)="row">
                    <div class="d-flex justify-content-between align-items-start" style="gap: 12px;">
                      <div style="min-width: 0; flex: 1;">
                        <h4 class="waiver-card__name">{{row.item.first_name}} {{row.item.last_name}}</h4>
                        <div class="waiver-card__when">
                          {{getDisplayTime(row.item)}}
                          <span class="waiver-card__when-sep">·</span>
                          {{getDate(row.item.booking_date)}}
                        </div>
                      </div>
                      <div class="waiver-card__badges">
                        <badge v-if="row.item.is_minor === 1" type="primary">minor</badge>
                        <badge v-if="row.item.not_participating === 1" type="danger">not participating</badge>
                        <badge v-if="row.item.custom_field_response_count > 0" type="default">
                          {{ row.item.custom_field_response_count }} {{ row.item.custom_field_response_count === 1 ? 'response' : 'responses' }}
                        </badge>
                        <i class="tim-icons icon-minimal-right waiver-card__chev" aria-hidden="true"></i>
                      </div>
                    </div>

                    <div class="waiver-card__event">
                      <img v-if="gameImageUrl(row.item.game_id)" class="waiver-card__event-img" v-twin-src="gameImageUrl(row.item.game_id)" alt="" />
                      <div v-else class="waiver-card__event-img waiver-card__event-img--placeholder"></div>
                      <div class="waiver-card__event-name">{{row.item.game || 'Walk-in'}}<badge v-if="!row.item.booking_slot_id && row.item.game" type="default" class="ml-2" style="font-size: 8px; padding-left: 7px; padding-right: 7px;">No booking</badge></div>
                    </div>

                    <div v-if="row.item.email" class="waiver-card__email">{{ row.item.email }}</div>
                  </template>
                </b-table>
                <b-pagination v-on:input="onPageChange()"
                              v-model="currentPage"
                              :total-rows="totalRows"
                              :per-page="perPage"
                              :limit="3"
                ></b-pagination>
              </div>
            </div>
          </card>
        </div>
      </div>
      <waiver-drawer
        :show="drawer.show"
        :waiver="drawer.waiver"
        @close="drawer.show = false"
        @changed="onWaiverChanged"
        @deleted="fetchWaivers(false)" />
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'element-ui';
  import { Auth } from 'aws-amplify'
  import { BTable, BPagination } from 'bootstrap-vue'
  import { API_LOCATION } from '../../api-config'
  const axios = require('axios')
  import {time, displayTime, formattedDate, dayOfWeek} from "@/plugins/dateFormatter";
  import Badge from "@/components/Badge";
  import {formatPhoneDisplay} from "@/util/phoneFormatter";
  import WaiverDrawer from "@/components/Waivers/WaiverDrawer.vue";

  export default {
    name: "Waivers",
    components: {
      Badge,
      WaiverDrawer,
      BTable,
      BPagination,
      Tooltip,
    },
    data() {
      return {
        feature_not_enabled: false,
        polling: null,
        loading: false,
        date_format: JSON.parse(localStorage.getItem('group')).date_format,
        time_format: JSON.parse(localStorage.getItem('group')).time_format,
        noDataAvailable: false,
        load: false,
        filterValues: this.buildInitialFilterValues(),
        // FilterBar (saved-views enabled) drives the first fetch via its initial `change`.
        didInitialFetch: false,
        filterBarKey: 0,
        refreshing: false,
        todayActive: false,
        waivers: [],
        perPage: Number(this.$route.query.count) || 10,
        currentPage: Number(this.$route.query.page) || 1,
        totalRows: 0,
        perPageOptions: [5, 10, 25, 50],
        fields: ['name', 'email', 'phone', { key: 'game', label: 'Event' }, 'booking_day' , 'booking_time'],
        mobile_fields: ['waivers'],
        gameOptions: '',
        drawer: { show: false, waiver: null },
      };
    },
    computed: {
      // Schema for the shared <filter-bar>. Primary chips are always visible; the rest sit
      // behind "More filters".
      filterSchema(){
        // "Walk-in" sits alongside the events as one more thing a waiver can belong to:
        // a waiver is tied to an event or it's a walk-in with no event, never both. The
        // 'walkin' value is translated to the waiver_type param at the fetch boundary.
        const gameOptions = [
          { label: 'Walk-in', value: 'walkin' },
          ...(Array.isArray(this.gameOptions) ? this.gameOptions.map(g => ({ label: g.name, value: g.id, group: 'Events' })) : []),
        ]
        return [
          { key: 'first_name', type: 'text', label: 'First name', placeholder: 'First name', operatorLabel: 'contains', primary: true },
          { key: 'last_name', type: 'text', label: 'Last name', placeholder: 'Last name', operatorLabel: 'contains', primary: true },
          { key: 'game', type: 'select', label: 'Event', options: gameOptions, primary: true },
          { key: 'date', type: 'daterange', label: 'Booking date', primary: true },
          { key: 'email', type: 'text', label: 'Email', placeholder: 'Email', operatorLabel: 'contains' },
          { key: 'phone', type: 'text', label: 'Phone', placeholder: 'Phone number', operatorLabel: 'contains' },
        ]
      },
      // Chip-styled shortcut toggles rendered inside the filter bar. Self-contained —
      // "today" filters to today's bookings via its own state, independent of the date chip.
      quickActions(){
        return [{ key: 'today', label: "Today's bookings", active: this.todayActive }]
      },
    },
    methods: {
      buildInitialFilterValues(){
        const q = this.$route.query || {}
        return {
          first_name: q.first_name || '',
          last_name: q.last_name || '',
          email: q.email || '',
          phone: q.phone || '',
          game: q.waiver_type === 'walkin' ? 'walkin' : (q.game ? Number(q.game) : ''),
          date: { mode: 'range', days: '', from: q.from || '', to: q.to || '' },
        }
      },
      computeDateRange(){
        const d = this.filterValues.date || {}
        let to = ''
        let from = ''
        if (d.mode === 'days' && d.days !== '' && d.days != null) {
          const today = new Date()
          const dt = new Date()
          dt.setDate(dt.getDate() - Number(d.days))
          const sd = dt.getFullYear() + '-' + ('0' + (dt.getMonth() + 1)).slice(-2) + '-' + ('0' + dt.getDate()).slice(-2)
          const ed = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2)
          from = new Date(sd).toISOString()
          to = new Date(ed).toISOString()
        } else if (d.mode === 'range' && d.from && d.to) {
          from = new Date(d.from).toISOString()
          to = new Date(d.to).toISOString()
        }
        return { to, from }
      },
      async fetchWaivers(resetPage, silent = true){
        if (resetPage) this.currentPage = 1
        let { to, from } = this.computeDateRange()
        // The "Today's bookings" quick toggle acts on its own — when on it scopes the fetch
        // to today, independent of (and overriding) the Booking date filter chip.
        if (this.todayActive) {
          const t = new Date()
          const iso = t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0') + '-' + String(t.getDate()).padStart(2, '0')
          from = new Date(iso).toISOString()
          to = new Date(iso).toISOString()
        }
        const count = this.perPage
        const offset = Number((this.currentPage * this.perPage) - this.perPage)
        // silent → no full-screen overlay (filter-bar shows its own inline spinner via refreshing).
        if (silent) this.refreshing = true
        try {
          const isWalkin = this.filterValues.game === 'walkin'
          await this.getWaivers(count, offset, this.filterValues.first_name, this.filterValues.last_name,
            this.filterValues.email, this.filterValues.phone, isWalkin ? '' : (this.filterValues.game || ''), to, from, silent, isWalkin ? 'walkin' : '')
        } finally {
          if (silent) this.refreshing = false
        }
      },
      onFiltersChanged(values){
        this.filterValues = { ...this.filterValues, ...values, date: { ...values.date } }
        // First change is FilterBar's initial saved-views resolution: keep the URL
        // page and use the full load. Later changes reset to page 1 (silent).
        if(!this.didInitialFetch){
          this.didInitialFetch = true
          this.fetchWaivers(false, false)
        } else {
          this.fetchWaivers(true)
        }
      },
      onPerPageChange(n){
        this.perPage = n
        this.fetchWaivers(true)
      },
      onPageChange(){
        this.fetchWaivers(false)
      },
      clearFilters(){
        this.todayActive = false
        // Drive the bar's own clear rather than remounting it (:key). A remount would
        // re-run saved-views precedence and re-apply the default/last-used, undoing
        // the clear. clearAll() empties the chips and emits `change`, which resets
        // filterValues and refetches via onFiltersChanged.
        if (this.$refs.filterBar && this.$refs.filterBar.clearAll) {
          this.$refs.filterBar.clearAll()
        } else {
          this.filterValues = { first_name: '', last_name: '', email: '', phone: '', game: '', date: { mode: 'range', days: '', from: '', to: '' } }
          this.filterBarKey++
          this.fetchWaivers(true)
        }
      },
      goToBilling(){
        this.$router.push('/settings/billing')
      },
      rowClass(){
        return this.hasPermissions('waivers_update') ? 'row-clickable' : ''
      },
      onRowClicked(item, index, event){
        // Don't open drawer if click was on an action menu, button, or link
        if (event && event.target && event.target.closest('button, a, input, .badge')) {
          return
        }
        if (!this.hasPermissions('waivers_update')) {
          return
        }
        this.drawer = { show: true, waiver: item }
      },
      onWaiverChanged(payload){
        // The toggle only flips a badge; anything that moves the waiver re-fetches the page.
        if (payload.action === 'participation') {
          const row = this.waivers.find(w => w.id === payload.waiverId)
          if (row) row.not_participating = payload.notParticipating ? 1 : 0
          return
        }
        this.fetchWaivers(false)
      },
      hasPermissions(scope){
        let permissions = JSON.parse(localStorage.getItem('permissions'))
        const matchFound = permissions.some(element => element === scope);
        return matchFound
      },
      getDayOfWeek(d){
        if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
          return dayOfWeek(d) + ', '
        }
        return ''
      },
      getDate(d){
        return formattedDate(this.date_format, d)
      },
      getTime(d){
        return time(this.time_format, d)
      },
      getDisplayTime(slot){
        // Walk-in waivers have no slot time. Show a dash rather than a formatted empty time.
        if(slot && !slot.start_time && Number(slot.is_all_day) !== 1){
          return '-'
        }
        return displayTime(slot, this.time_format)
      },
      formatPhone(phone){
        return formatPhoneDisplay(phone)
      },
      onQuickAction(key){
        if (key === 'today') {
          // Self-contained toggle — does not touch the Booking date filter.
          this.todayActive = !this.todayActive
          this.fetchWaivers(true)
        }
      },
      getAuthToken() {
        return new Promise ( function(resolve) {
          Auth.currentSession()
            .then(data =>{
              resolve(data.idToken.jwtToken)
            })
            .catch(err => console.log(err))
        })
      },
      async getGames () {
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
        axios.get(url, config)
          .then(response => {
            this.loading = false
            this.gameOptions = response.data
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            if(err.response.status == 401){
              this.$router.push('/forbidden')
            }
            else if(err.response.status == 500){
              this.feature_not_enabled = true
            }
            else {
              this.$notify({ type: 'danger', message: 'Something went wrong' })
            }
          })
      },
      async getWaivers (count, offset, firstName, lastName, email, phone, game, to, from, no_load, waiverType) {
        if(firstName === undefined){
          firstName = ''
        }
        if(lastName === undefined){
          lastName = ''
        }
        if(email === undefined){
          email = ''
        }
        if(phone === undefined){
          phone = ''
        }
        if(game === undefined){
          game = ''
        }
        if(to === undefined){
          to = ''
        }
        if(from === undefined){
          from = ''
        }
        if(waiverType === undefined){
          waiverType = ''
        }

        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/waivers?count=' + count + "&offset=" + offset + "&first_name=" + encodeURIComponent(firstName) +
          "&last_name=" + encodeURIComponent(lastName) + "&email=" + encodeURIComponent(email) + "&phone=" + encodeURIComponent(phone) + "&game=" + encodeURIComponent(game) + "&to=" + to + "&from=" + from + "&waiver_type=" + encodeURIComponent(waiverType)
        this.load = false
        await this.getWaiverURL(url, config, no_load)

        this.load = true
      },
      getWaiverURL(url, config, no_load){

        if(!no_load){
          this.loading = true
        }
          this.axios.get(url, config)
            .then(response => {
              this.loading = false
              if(this.waivers.length <=0){
                this.currentPage = Number(this.$route.query.page)
              }
              this.waivers = response.data.waivers

              if(this.waivers.length <= 0){
                this.noDataAvailable = true
              } else {
                this.noDataAvailable = false
              }

              this.totalRows = response.data.totalRows
              this.addQueryParameters()

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              }
              else if(err.response.status == 500){
                this.feature_not_enabled = true
              }
              else {
                this.$notify({ type: 'danger', message: 'Something went wrong' })
              }
            })
      },
      escapeCSVValue(value) {
        if (typeof value === 'string') {
          // Escape double quotes by doubling them
          value = value.replace(/"/g, '""');

          // If the value contains a comma, wrap it in double quotes
          if (value.includes(',') || value.includes('\n')) {
            value = `"${value}"`;
          }
        }
        return value;
      },
      async downloadCSV(){
        //get the entire data set
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }

        this.loading = true
        // Page size matches the server-side cap for the lean export path (export=1),
        // so a typical filtered export is a single request and large ones are a handful.
        const count = 5000
        const { to, from } = this.computeDateRange()
        let csvData
        try {
          csvData = await this.getAllWaivers(config, count, this.filterValues.first_name,
            this.filterValues.last_name, this.filterValues.email, this.filterValues.phone,
            this.filterValues.game || '', to, from)
        } catch (err) {
          this.loading = false
          console.log(err)
          if (err.response && err.response.status == 401) {
            this.$router.push('/')
          } else {
            // Fail loud — never hand the user a silently partial export.
            this.$notify({ type: 'danger', message: 'The export could not be completed. Please try again.' })
          }
          return
        }
        this.loading = false

        let csv = 'First Name,Last Name,Email,Phone,Event,Booking Day,Booking Time\n';
        csvData.forEach(row => {
          csv += [this.escapeCSVValue(row.first_name), this.escapeCSVValue(row.last_name),
            this.escapeCSVValue(row.email), this.escapeCSVValue(row.phone),
            this.escapeCSVValue(row.game || 'Walk-in'),
            row.booking_date ? this.getDate(row.booking_date.substring(0,10)) : '',
            this.getDisplayTime(row)]
          csv += "\n";
        });

        // Blob download — avoids the URL-length ceiling of a data: URI on large exports.
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = 'waivers.csv';
        anchor.click();
        URL.revokeObjectURL(objectUrl);
      },
      async getAllWaivers(config, count, firstName, lastName, email, phone, game, to, from){
        if(firstName === undefined){
          firstName = ''
        }
        if(lastName === undefined){
          lastName = ''
        }
        if(email === undefined){
          email = ''
        }
        if(phone === undefined){
          phone = ''
        }
        if(game === undefined){
          game = ''
        }
        if(to === undefined){
          to = ''
        }
        if(from === undefined){
          from = ''
        }
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id

        // Page offsets are computed up front from the already-known filtered total
        // (set by the list fetch). export=1 hits the lean server path; skip_count=1 on
        // every page since we don't need the server to recompute the total here.
        const offsets = []
        for (let offset = 0; offset < this.totalRows; offset += count) {
          offsets.push(offset)
        }

        const filterParams = "&first_name=" + encodeURIComponent(firstName) +
          "&last_name=" + encodeURIComponent(lastName) + "&email=" + encodeURIComponent(email) +
          "&phone=" + encodeURIComponent(phone) + "&game=" + encodeURIComponent(game) +
          "&to=" + to + "&from=" + from + "&export=1&skip_count=1"

        // Bounded concurrency: cap simultaneous in-flight requests so large exports
        // don't fan out into a burst of concurrent lambda invocations against the DB.
        // A failed page rejects — the caller aborts rather than producing a partial file.
        const pages = new Array(offsets.length)
        let cursor = 0
        const worker = async () => {
          while (cursor < offsets.length) {
            const i = cursor++
            const offset = offsets[i]
            const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
              '/waivers?count=' + count + "&offset=" + offset + filterParams
            const response = await axios.get(url, config)
            pages[i] = response.data.waivers
          }
        }
        const CONCURRENCY = 3
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, offsets.length) }, () => worker())
        )

        // merge all pages, in order
        let arr = []
        pages.forEach((page) => {
          arr = arr.concat(page)
        })
        return arr
      },
      gameImageUrl(gameId){
        if (!gameId || !Array.isArray(this.gameOptions)) return null
        const game = this.gameOptions.find(g => g.id === gameId)
        if (!game) return null
        // Pattern 1: flat `url` field on the game (used by GameDashboards, GameStats, GameInstance)
        if (game.url) return game.url
        // Pattern 2: nested `game_images` JSON array (used by companies-get-games-for-company-group)
        if (game.game_images) {
          try {
            const images = typeof game.game_images === 'string'
              ? JSON.parse(game.game_images)
              : game.game_images
            if (Array.isArray(images) && images.length > 0 && images[0].url) return images[0].url
          } catch (e) { /* fall through */ }
        }
        // Pattern 3: legacy `img` field if it happens to be an absolute URL
        if (game.img && typeof game.img === 'string' && /^https?:\/\//.test(game.img)) {
          return game.img
        }
        return null
      },
      getOffset(){
        return Number((this.currentPage * this.perPage) - this.perPage)
      },
      addQueryParameters() {
        this.$nextTick(() => {
          this.currentPage = this.$route.query.page
        });
        const d = this.filterValues.date || {}
        const newQuery = {
          ...this.$route.query,
          count: this.perPage,
          page: this.currentPage,
          first_name: this.filterValues.first_name,
          last_name: this.filterValues.last_name,
          email: this.filterValues.email,
          phone: this.filterValues.phone,
          game: this.filterValues.game === 'walkin' ? '' : (this.filterValues.game || ''),
          waiver_type: this.filterValues.game === 'walkin' ? 'walkin' : '',
          to: d.to || '',
          from: d.from || '',
        }
        this.$router.replace({query: newQuery}).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
      }
    },
    mounted() {
      this.perPage = Number(this.$route.query.count) || 10
      this.currentPage = Number(this.$route.query.page) || 1
      // filterValues was seeded from the URL query in data() (buildInitialFilterValues).
      // FilterBar owns the initial applied filter state (saved-views precedence) and
      // emits one initial `change` that drives the first fetch — see onFiltersChanged.
      // Show the loading overlay until that lands.
      this.loading = true
      this.polling = setInterval(() => {
        const { to, from } = this.computeDateRange()
        const isWalkin = this.filterValues.game === 'walkin'
        this.getWaivers(this.perPage, this.getOffset(), this.filterValues.first_name,
          this.filterValues.last_name, this.filterValues.email, this.filterValues.phone,
          isWalkin ? '' : (this.filterValues.game || ''), to, from, 1, isWalkin ? 'walkin' : '')
      }, 60000);

      this.getGames()
    },
    beforeDestroy() {
      // Clear the interval when the component is destroyed
      clearInterval(this.polling);
    },
  };
</script>
<style scoped>
  /* Mobile horizontal padding cleanup — Bootstrap's default col-12 + card-body
     + table cell padding stacks up to ~50px on each side, which wastes screen
     real estate on small phones. Tighten just inside the mobile wrap so we
     don't affect the desktop view. !important needed to overpower Bootstrap's
     base specificity. */
  .waivers-mobile-wrap > .col-12 {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
  .waivers-mobile-wrap ::v-deep .card-body {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
  .waivers-mobile-wrap ::v-deep .table td,
  .waivers-mobile-wrap ::v-deep .table th {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  /* Mobile waiver card layout */
  .waiver-card__name {
    margin: 0 0 2px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
    word-break: break-word;
  }
  .waiver-card__when {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.3;
  }
  .waiver-card__when-sep {
    margin: 0 6px;
    color: rgba(255, 255, 255, 0.35);
  }
  .waiver-card__badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    max-width: 60%;
    flex-shrink: 0;
  }
  .waiver-card__chev {
    margin-left: 6px;
    font-size: 12px;
    color: rgba(225, 78, 202, 0.65);
    vertical-align: middle;
  }
  .waiver-card__event {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    min-width: 0;
  }
  .waiver-card__event-img {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.04);
  }
  .waiver-card__event-img--placeholder {
    background: rgba(255, 255, 255, 0.04);
  }
  .waiver-card__event-name {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .waiver-card__email {
    margin-top: 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .empty-state-hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 14px;
  }

  /* Mobile list skeleton */
  .waiver-skel-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 8px 0;
  }
  .waiver-skel-card {
    padding: 12px 14px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.02);
  }

  /* Light mode */
  .white-content .waiver-card__name { color: rgba(0, 0, 0, 0.9); }
  .white-content .waiver-card__when { color: rgba(0, 0, 0, 0.75); }
  .white-content .waiver-card__when-sep { color: rgba(0, 0, 0, 0.3); }
  .white-content .waiver-card__event-img,
  .white-content .waiver-card__event-img--placeholder {
    border-color: rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.04);
  }
  .white-content .waiver-card__event-name { color: rgba(0, 0, 0, 0.65); }
  .white-content .waiver-card__email { color: rgba(0, 0, 0, 0.6); }
  .white-content .empty-state-hint { color: rgba(0, 0, 0, 0.65); }
</style>
